import { twMerge } from 'tailwind-merge';

type StagePosition = {
  left: number;
  width: number;
  center: number;
};

type ComponentVariant = 'singleEvent' | 'referral';

type ProgressBarProps = {
  stagePositions: StagePosition[];
  currentStageIndex: number;
  containerWidth: number;
  componentVariant?: ComponentVariant;
};

type ProgressBarBulletProps = {
  variant?: 'past' | 'current' | 'future';
  style?: React.CSSProperties;
  componentVariant?: ComponentVariant;
};

const ProgressBarBullet = ({ variant = 'past', style, componentVariant = 'singleEvent' }: ProgressBarBulletProps) => {
  const variantStyles = {
    singleEvent: {
      bullet: {
        past: {
          backgroundColor: 'tw-bg-[#1F408C]',
          bulletColor: 'tw-bg-white',
        },
        current: {
          backgroundColor: 'tw-bg-[#35EBCF]',
          bulletColor: 'tw-bg-[#051943]',
        },
        future: {
          backgroundColor: 'tw-bg-[#1F408C]',
          bulletColor: 'tw-bg-[#051943]',
        },
      },
    },
    referral: {
      bullet: {
        past: {
          backgroundColor: 'tw-bg-[#3170FF]',
          bulletColor: 'tw-bg-white',
        },
        current: {
          backgroundColor: 'tw-bg-[#379AFF]',
          bulletColor: 'tw-bg-[#051943]',
        },
        future: {
          backgroundColor: 'tw-bg-[#2D5EFF]',
          bulletColor: 'tw-bg-[#051943]',
        },
      },
    },
  };

  return (
    <div
      className={twMerge(
        'tw-absolute tw-top-[-6px] tw-z-[3] tw-flex tw-h-[22px] tw-w-[22px] tw-items-center tw-justify-center tw-rounded-full',
        variantStyles[componentVariant].bullet[variant].backgroundColor
      )}
      style={style}
    >
      <div
        className={twMerge(
          'tw-top-1/2 tw-h-[10px] tw-w-[10px] tw-rounded-full',
          variantStyles[componentVariant].bullet[variant].bulletColor
        )}
      />
    </div>
  );
};

export const ProgressBar = ({
  stagePositions,
  currentStageIndex,
  containerWidth,
  componentVariant = 'singleEvent',
}: ProgressBarProps) => {
  if (stagePositions.length === 0 || containerWidth === 0) return null;

  const currentStageCenter = stagePositions[currentStageIndex]?.center || stagePositions[0].center;

  // Calculate progress percentage based on current stage position relative to container width
  const progressPercentage = (currentStageCenter / containerWidth) * 100;

  // variant can be past, current, future
  const getBulletVariant = (index: number) => {
    if (index === currentStageIndex) return 'current';
    if (index < currentStageIndex) return 'past';
    return 'future';
  };

  const variantStyles = {
    singleEvent: {
      background: 'tw-bg-[#1F408C]',
      progress: 'tw-bg-currentStageGradient',
    },
    referral: {
      background: 'tw-bg-[#1F408C]',
      progress: 'tw-bg-gamefieldProgressGradient',
    },
  };

  return (
    <div className="tw-relative">
      {/* Main progress line - full width */}
      <div
        className={twMerge(
          'tw-relative tw-z-[1] tw-h-[8.4px] tw-w-full tw-rounded-full',
          variantStyles[componentVariant].background
        )}
      />

      {/* Progress fill - percentage of full width */}
      <div
        className={twMerge(
          'tw-absolute tw-left-0 tw-top-0 tw-z-[2] tw-h-[8.4px] tw-rounded-full',
          variantStyles[componentVariant].progress
        )}
        style={{
          width: `${progressPercentage}%`,
        }}
      />

      {/* Stage bullets - positioned at actual stage centers */}
      {stagePositions.map((position, index) => (
        <ProgressBarBullet
          key={index}
          style={{
            left: `${position.center - 8}px`,
          }}
          variant={getBulletVariant(index)}
          componentVariant={componentVariant}
        />
      ))}
    </div>
  );
};
